import ApiService from '@/services/apiService';
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import listMixin from '../../niotaClassic/listMixin';
import listOptionsPersistenceMixin from '@/components/custom/listOptionsPersistenceMixin';
import iconsMixin from '@/components/core/dtwin/state/bridge/iconsMixin';
import MultiSearch from '@/components/core/MultiSearch/MultiSearch.vue';
import virtualDeviceSearchConfig from '@/components/core/MultiSearch/Mixins/virtualDeviceSearchConfig';
import { isCopyToClipboardSupported, copyToClipboard } from '@/utils/vuex';
import get from 'lodash/get';
import set from 'lodash/set';
import debounce from 'lodash/debounce';
import axios from 'axios';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
const ID_ONLY_SEARCH_PARAMS = ['deviceDriver', 'deviceType'];
function isValidSearchItem(field, search) {
  if (!ID_ONLY_SEARCH_PARAMS.includes(field)) {
    return true;
  }
  const value = search && typeof search === 'object' ? search.value : search;
  return /^\d+$/.test(`${value !== null && value !== void 0 ? value : ''}`.trim());
}
export default {
  name: 'CoreVirtualDeviceList',
  components: {
    MultiSearch
  },
  mixins: [listMixin, listOptionsPersistenceMixin, iconsMixin, virtualDeviceSearchConfig],
  props: {
    favouriteFilterEnabled: {
      type: Boolean,
      required: true
    },
    accounts: {
      type: Array,
      default: () => [],
      required: false
    },
    predefinedSearch: {
      type: Array,
      default: () => [],
      required: false
    },
    // if used in the device-type list, do not restore the last filter from the virtual-device-list
    // should show ALL unfiltered devices by default
    restoreFilter: {
      type: Boolean,
      required: false,
      default: () => true
    },
    containerClass: {
      type: String,
      default: () => 'n20-mainrow',
      required: false
    },
    deviceTemplate: {
      type: Object,
      default: () => ({}),
      required: false
    },
    initSearchItems: {
      type: Array,
      default: undefined,
      required: false
    },
    initOperator: {
      type: String,
      default: undefined,
      required: false
    }
  },
  data: function () {
    var _this$$store$state$ap, _this$$store$state$ap2;
    // Use initSearchItems/initOperator if provided, otherwise fall back to store
    const storeSearchItems = ((_this$$store$state$ap = this.$store.state.app.pageFilters) === null || _this$$store$state$ap === void 0 || (_this$$store$state$ap = _this$$store$state$ap.VirtualDevice) === null || _this$$store$state$ap === void 0 || (_this$$store$state$ap = _this$$store$state$ap.list) === null || _this$$store$state$ap === void 0 ? void 0 : _this$$store$state$ap.searchDetails) || [];
    const storeOperator = ((_this$$store$state$ap2 = this.$store.state.app.pageFilters) === null || _this$$store$state$ap2 === void 0 || (_this$$store$state$ap2 = _this$$store$state$ap2.VirtualDevice) === null || _this$$store$state$ap2 === void 0 || (_this$$store$state$ap2 = _this$$store$state$ap2.list) === null || _this$$store$state$ap2 === void 0 ? void 0 : _this$$store$state$ap2.searchOperator) || 'AND';
    return {
      deleteDialog: false,
      deletePrompt: false,
      // "foreign" dependencies of a devicetwin that should be deleted
      deviceDependencies: {},
      deviceObject: null,
      deviceDeleteRemotely: false,
      disableFetchOnCreatedMixin: true,
      selectedAccounts: [],
      lastSearch: null,
      searchItems: (this.initSearchItems !== undefined ? this.initSearchItems : storeSearchItems).filter(item => isValidSearchItem(item.param, item.search)),
      searchOperator: this.initOperator !== undefined ? this.initOperator : storeOperator,
      listPersistencePath: 'VirtualDevice.list',
      searchMode: 'multi',
      favouriteMode: 'on',
      showDialogCreate: false,
      isFetching: true,
      tableOptions: {
        ...defaultTableOptions
      },
      defaultTableOptions: {
        ...defaultTableOptions
      },
      selected: [],
      visibleHeaderValues: [],
      customizableColumns: [],
      fetchList: () => {},
      latestRequestId: 0,
      cancelTokenSource: null,
      isFirstLoad: true,
      preventNextFetch: false,
      localUser: {},
      hasInitializedItemsPerPage: false,
      hasInitializedColumns: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions', 'language']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled',
      sessionUserId: 'authentication/userId'
    }),
    userId() {
      return get(this, '$store.state.authentication.user.id') || null;
    },
    isVirtualDeviceManager() {
      return this.permissions['VirtualDevice.manage'];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isEmptyList() {
      return this.predefinedSearch.length === 0 && this.searchItems.length === 0 && this.serverItemsLength <= 0 && this.isFetching === false;
    },
    headerBG() {
      return './img/consumer.png';
    },
    selectedRows() {
      return this.list.filter(item => item.selected);
    },
    computedVisibleHeaders() {
      return this.tableHeaders.filter(header => {
        // icon and actions are always visible
        if (header.show === undefined) {
          return true;
        }
        return this.visibleHeaderValues.includes(header.value);
      });
    },
    tableHeaders() {
      return [{
        sortable: false,
        value: 'icon'
        // hide: 'mdAndDown',
      }, {
        text: `${this.$i18n_t('Common.id')}`,
        value: 'id',
        sortable: true,
        show: true
      }, {
        text: `${this.$i18n_t('DigitalTwin.VirtualDevice.props.title')}`,
        value: 'title',
        sortable: true,
        show: true
      }, {
        text: `${this.$i18n_t('DigitalTwin.VirtualDevice.props.account')}`,
        value: 'tenant.name',
        sortable: false,
        show: false
      }, {
        text: `${this.$i18n_t('DigitalTwin.VirtualDevice.props.address')}`,
        value: 'data.address.formatted_address',
        sortable: true,
        show: false
      }, {
        text: `${this.$i18n_t('DigitalTwin.VirtualDevice.props.createdAt')}`,
        value: 'createdAt',
        sortable: true,
        show: false
      }, {
        text: `${this.$i18n_t('DigitalTwin.VirtualDevice.props.updatedAt')}`,
        value: 'updatedAt',
        sortable: true,
        show: false
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.type'),
        value: 'bridge.type',
        sortable: true,
        // hide: 'mdAndDown',
        show: true
      }, this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback') ? {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceType'),
        value: 'deviceType',
        sortable: true,
        show: true
      } : {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceDriver'),
        value: 'deviceDriver',
        sortable: true,
        show: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceTemplate'),
        value: 'deviceTemplate',
        sortable: true,
        show: true
      }, {
        text: this.$i18n_t('IS.AssetMgmt.list.lastSeen'),
        value: 'lastSeen',
        // hide: 'mdAndDown',
        sortable: true,
        show: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceId'),
        value: 'deviceId',
        sortable: true,
        show: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.operationalStatusShort'),
        value: 'operationalStatus',
        // hide: 'mdAndDown',
        sortable: true,
        show: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.health'),
        value: 'health',
        sortable: true,
        show: true
      }, {
        text: '',
        value: 'actions',
        sortable: false,
        width: '60',
        align: 'right'
      }].filter(item => {
        return !item.hide || !this.$vuetify.breakpoint[item.hide];
      });
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [10, defaultItemsPerPage, 20, 50, 100, 200]
      };
    },
    featureFlagDeviceTypeFallbackEnabled() {
      return this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    canDeleteDevice() {
      return this.deviceDependencies && (get(this.deviceDependencies, 'aggregatedStates') || []).length === 0;
    }
  },
  watch: {
    favouriteFilterEnabled: {
      handler: function () {
        if (!this.preventNextFetch) {
          this.fetchList(true);
        } else {
          this.preventNextFetch = false;
        }
      }
    },
    accounts: {
      handler: function () {
        if (!this.preventNextFetch) {
          this.fetchList(true);
        } else {
          this.preventNextFetch = false;
        }
      }
    },
    'tableOptions.page': {
      handler: function (page) {
        sessionStorage.setItem('vd_list_page', page);
      }
    },
    'tableOptions.itemsPerPage': async function (newValue, oldValue) {
      if (!this.hasInitializedItemsPerPage) {
        this.hasInitializedItemsPerPage = true;
        return;
      }
      if (newValue !== oldValue) {
        const data = {
          id: this.sessionUserId,
          properties: {
            userDefaults: {
              virtualDevicesListItemsPerPage: newValue
            }
          }
        };
        await ApiService.updateUserPartial(data);
        this.$store.commit('app/setRowsPerPage', newValue);
      }
    },
    initSearchItems: {
      handler: function (val) {
        if (val && val.length > 0 && this.$refs.multiSearch) {
          this.$refs.multiSearch.loadRules({
            rules: val.map(item => ({
              field: item.param,
              value: item.search,
              key: item.key,
              condition: item.condition
            })),
            join: this.initOperator || 'AND'
          });
        }
        this.preventNextFetch = true;
      },
      immediate: false
    },
    initOperator: {
      handler: function (val) {
        if (this.initSearchItems && this.initSearchItems.length > 0 && this.$refs.multiSearch) {
          this.$refs.multiSearch.loadRules({
            rules: this.initSearchItems.map(item => ({
              field: item.param,
              value: item.search,
              key: item.key,
              condition: item.condition
            })),
            join: val || 'AND'
          });
        }
        this.preventNextFetch = true;
      },
      immediate: false
    }
  },
  created() {
    this.isFetching = true;
    this.fetchList = debounce(this._fetchList, 500);
    this.$parent.$on('close-step-form', () => this.selected = []);
  },
  async mounted() {
    this.localUser = await ApiService.getUser(this, this.$store.state.authentication.user.id);
    const userDefaults = get(this.localUser, 'properties.userDefaults');
    this.tableOptions.itemsPerPage = userDefaults.virtualDevicesListItemsPerPage || parseInt(this.$store.state.app.rowsPerPage) || 20;
    this.setInitialCustomizableColumns();
    this.initializeColumns();

    // Initialize MultiSearch UI with correct rules and operator
    if (this.$refs.multiSearch && this.initSearchItems && this.initSearchItems.length > 0) {
      this.$refs.multiSearch.loadRules({
        rules: this.initSearchItems.map(item => ({
          field: item.param,
          value: item.search,
          key: item.key,
          condition: item.condition
        })),
        join: this.initOperator || 'AND'
      });
    }
  },
  methods: {
    get,
    showDeleteExternalService(item) {
      const type = get(item, 'properties.bridge.type', null);
      return !['bridge-mqtt', 'bridge-incomingwebhook', 'bridge-webhook', 'bridge-wireless-mbus-decoder'].includes(type);
    },
    setInitialCustomizableColumns() {
      const vdListCols = get(this.localUser, 'properties.userDefaults.vdListCols', []);
      this.customizableColumns = this.tableHeaders.filter(header => {
        return header.show !== undefined && !header.hide;
      }).map(header => {
        return {
          text: header.text,
          value: header.value,
          show: header.show,
          visible: vdListCols.includes(header.value)
        };
      });
    },
    initializeColumns() {
      try {
        let vdListCols = get(this.localUser, 'properties.userDefaults.vdListCols', []);
        if (vdListCols.length === 0) {
          vdListCols = this.tableHeaders.filter(header => header.show).map(header => header.value);
        }

        // Set visible columns to saved columns
        this.visibleHeaderValues = vdListCols;

        // Set customizable columns `show` to true for saved columns
        this.customizableColumns.map(column => {
          column.show = this.visibleHeaderValues.includes(column.value);
        });
      } catch (e) {
        // Set visible columns to default columns
        this.handleColumnUpdate(this.tableHeaders);
      }
    },
    async handleColumnUpdate(columns) {
      if (!this.hasInitializedColumns) {
        this.hasInitializedColumns = true;
        return;
      }
      this.visibleHeaderValues = columns.filter(column => column.show).map(column => column.value);
      set(this.localUser, 'properties.userDefaults.vdListCols', this.visibleHeaderValues);
      await ApiService.updateUser(this.localUser);
    },
    showParentsCreateDialog() {
      this.$parent.showDialogCreate = true;
    },
    showDeletePrompt(item) {
      this.deviceObject = item;
      this.deviceDeleteRemotely = false;
      this.fetchDeviceDependencies(this.deviceObject);
      this.deletePrompt = true;
    },
    setSelected(rows) {
      this.$emit('update-selected-rows', rows);
    },
    initializedDefaultPageFilters() {
      this.$emit('loaded-favourite-filter', this.favouriteFilter || false);
      if (!this.disableFetchOnCreatedMixin) {
        this.fetchList();
      }
    },
    isCopySupported() {
      return isCopyToClipboardSupported();
    },
    copyToClipboard(event, copyValue) {
      event.stopPropagation();
      copyToClipboard(copyValue);
    },
    relativeTime(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).fromNow();
      }
      return '-';
    },
    _fetchList(fromStart = false) {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Operation canceled due to a new request.');
      }
      this.cancelTokenSource = axios.CancelToken.source();
      const requestId = ++this.latestRequestId;
      this.isFetching = true;
      const tableOptions = fromStart ? {
        ...this.tableOptions,
        page: 1
      } : this.tableOptions;
      // persist options for next call of this page (during this browser-tab-session)
      this.$store.state.app.pageFilters = {
        ...this.$store.state.app.pageFilters,
        VirtualDevice: {
          ...(get(this.$store.state.app, 'pageFilters.VirtualDevice') || {}),
          list: {
            searchDetails: this.searchItems,
            searchOperator: this.searchOperator,
            tableOptions,
            favouriteFilter: this.favouriteFilterEnabled || false
          }
        }
      };

      // the bulk-editing from the list-component also calls the fetch-method,
      // it should have no effect on the DISPLAYED result, so no processing or persisting
      // thus, wrap the call to the backend here, to re-use it
      this.fetchResult(fromStart).then(result => {
        if (requestId !== this.latestRequestId) {
          return;
        }
        this.list = result.virtualDevices;
        this.serverItemsLength = result.totalCount;
        this.$emit('load-items', result);
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
          const storedPage = parseInt(sessionStorage.getItem('vd_list_page'), 10);
          if (!isNaN(storedPage)) {
            this.tableOptions.page = storedPage;
          }
        }
      }).catch(error => {
        if (requestId === this.latestRequestId && !axios.isCancel(error)) {
          this.$emit('load-items-error', error);
          console.error('Error fetching virtual devices', error);
        }
      }).finally(() => {
        if (requestId === this.latestRequestId) {
          this.isFetching = false;
        }
      });
    },
    // use `paginationMode = 'load-all' to disable a LIMIT
    fetchResult(fromStart = false, paginationMode = null) {
      const searchItems = (this.predefinedSearch || []).concat(this.searchItems).map(item => {
        if (!item.param) {
          return {};
        }
        return {
          variable: item.param,
          // support item.search.value === null, so do not simply have a >search.value || search< expression
          search: (item.search || {}).value === undefined ? item.search : item.search.value,
          key: item.key,
          condition: item.param && item.condition
        };
      }).filter(item => isValidSearchItem(item.variable, item.search));
      const tableOptions = fromStart ? {
        ...this.tableOptions,
        page: 1
      } : this.tableOptions;
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = tableOptions;
      let sortItem = sortBy && sortBy.length ? sortBy[0] : 'id';
      if (sortItem === 'data.address.formatted_address') {
        sortItem = 'address';
      }
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : false;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedPage = page ? page - 1 : 0;
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      const filters = [{
        type: 'tenantIds',
        ids: this.accounts.map(acc => acc.id)
      }];
      return ApiService.virtualDeviceFetch(this,
      // context
      null,
      // userId
      [],
      // scopeIds
      filters, this.favouriteFilterEnabled ? ['favourites'] : null, {
        variable: sortItem,
        direction: sortDirection
      },
      // orderBy
      usedPage * usedItemsPerPage,
      // offset
      paginationMode === 'load-all' ? null : usedItemsPerPage,
      // limit
      searchItems, undefined, undefined,
      // include identifiers
      undefined,
      // include properties
      undefined,
      // include drivers
      this.searchOperator, {
        cancelToken: this.cancelTokenSource.token
      }).then(result => {
        this.$emit('load-items', result);
        return result;
      });
    },
    retryLoadItems(fromStart = true) {
      this._fetchList(fromStart);
    },
    onSearchUpdate(search) {
      const rules = (search || {}).rules || [];
      const join = (search || {}).join || 'AND';
      this.searchItems = rules.map(rule => ({
        param: rule.field,
        search: rule.value,
        key: rule.key,
        condition: rule.condition
      })).filter(item => isValidSearchItem(item.param, item.search));
      this.searchOperator = join;
      this.fetchList(true);
      this.$emit('search-updated', rules, join);
    },
    fetchDeviceDependencies(virtualDevice) {
      this.deviceDependencies = null;
      return ApiService.virtualDeviceGetDependencies(virtualDevice.id).then(deviceDependencies => {
        deviceDependencies.aggregatedStates.forEach(state => {
          state.children = [{
            ...state.dtwin,
            icon: state.dtwin.twinType === 'virtualDevice' ? 'sensor-on' : 'dice-d8',
            isTwin: true
          }];
          state.title = `${state.title} (${state.key})`;
          state.icon = state.icon || 'layer-group';
        });
        this.deviceDependencies = deviceDependencies;
      });
    },
    deleteDevice(selected) {
      this.deleteDialog = true;
      ApiService.virtualDeviceDelete(selected.id, this.deviceDeleteRemotely).then(() => {
        this.deletePrompt = false;
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.deleteSuccess', 'success'));
        this.fetchList();
      }).catch(error => {
        console.error(error);
        const errorTranslateKey = `Error.${get(error, 'response.data.message')}`;
        if (error.response && this.$i18n_t(errorTranslateKey) !== errorTranslateKey) {
          this.$snack(this.$i18n_t(errorTranslateKey), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.deleteFailed'), 'error');
        }
      }).finally(() => {
        this.deleteDialog = false;
      });
    }
  }
};